"use client";

import { useMemo } from "react";
import {
  getSkillCategoryVisual,
  groupSkillsByCategory,
  type SkillCategorySlug,
} from "@/lib/skills/categories";
import { cn, formatSkillLabel } from "@/lib/utils";
import { SectionBody, SectionCard } from "../section-layout";
import { Icon } from "@/components/ui/icon";
import { HighlightedText } from "@/features/arbitrator/components/review/highlighted-text";
import { useProfile } from "../context";

/**
 * Per-category icon/label classes that work on both light and dark surfaces.
 * The dark: variants preserve the original dark-UI colors from `getSkillCategoryVisual`.
 * Light variants use darker Tailwind palette shades for sufficient contrast on white.
 */
const CATEGORY_THEMED: Record<SkillCategorySlug, { iconClass: string; labelClass: string }> = {
  web3:               { iconClass: "text-violet-600 dark:text-[#c4b5fd]",  labelClass: "text-violet-600 dark:text-[#c4b5fd]" },
  testing:            { iconClass: "text-green-700 dark:text-[#4ade80]",   labelClass: "text-green-700 dark:text-[#4ade80]" },
  systems_admin:      { iconClass: "text-amber-600 dark:text-[#fbbf24]",   labelClass: "text-amber-600 dark:text-[#fbbf24]" },
  network_security:   { iconClass: "text-blue-600 dark:text-[#60a5fa]",    labelClass: "text-blue-600 dark:text-[#60a5fa]" },
  mobile:             { iconClass: "text-pink-600 dark:text-[#f472b6]",    labelClass: "text-pink-600 dark:text-[#f472b6]" },
  front_end:          { iconClass: "text-sky-600 dark:text-[#38bdf8]",     labelClass: "text-sky-600 dark:text-[#38bdf8]" },
  devops:             { iconClass: "text-cyan-700 dark:text-[#22d3ee]",    labelClass: "text-cyan-700 dark:text-[#22d3ee]" },
  databases:          { iconClass: "text-emerald-700 dark:text-[#34d399]", labelClass: "text-emerald-700 dark:text-[#34d399]" },
  data_science:       { iconClass: "text-purple-600 dark:text-[#c084fc]",  labelClass: "text-purple-600 dark:text-[#c084fc]" },
  cloud_technologies: { iconClass: "text-blue-500 dark:text-[#93c5fd]",    labelClass: "text-blue-500 dark:text-[#93c5fd]" },
  back_end:           { iconClass: "text-orange-600 dark:text-[#fb923c]",  labelClass: "text-orange-600 dark:text-[#fb923c]" },
  ai_ml:              { iconClass: "text-fuchsia-600 dark:text-[#e879f9]", labelClass: "text-fuchsia-600 dark:text-[#e879f9]" },
  other:              { iconClass: "text-zinc-500 dark:text-[#a1a1aa]",    labelClass: "text-zinc-500 dark:text-[#a1a1aa]" },
};

export function SkillsSection({
  skills,
  firstName,
}: {
  skills: string[];
  firstName?: string;
}) {
  const { derived } = useProfile();
  // Callers (e.g. the share-page ProfileSheet) may pass an explicit name; fall
  // back to the context-derived one otherwise.
  const displayName = firstName ?? derived.firstName;

  const grouped = useMemo(() => groupSkillsByCategory(skills), [skills]);

  if (skills.length === 0) return null;

  return (
    <SectionCard title="Skill Map">
      <SectionBody>
        <div className="flex w-full min-w-0 flex-col items-stretch gap-4">
          <p className="text-sm font-normal leading-5 text-contrast/60">
            Arbi categorizes {displayName}&apos;s skills for your
            convenience.
          </p>
          <div
            className="flex w-full min-w-0 flex-col gap-3"
            data-evidence-source="skills"
          >
            {grouped.map((cat) => {
              const visual = getSkillCategoryVisual(cat.slug);
              const themed = CATEGORY_THEMED[cat.slug];
              return (
                <div
                  key={cat.slug}
                  className="flex w-full min-w-0 flex-col gap-2.5 rounded-lg border border-border bg-surface-elevated p-3"
                >
                  <div className="flex w-full min-w-0 items-center gap-2">
                    <Icon
                      name={visual.icon}
                      size={16}
                      className={themed.iconClass}
                    />
                    <p
                      className={cn("text-xs font-semibold tracking-[0.02em]", themed.labelClass)}
                    >
                      {cat.name}
                    </p>
                  </div>
                  <div className="flex w-full min-w-0 flex-wrap gap-1.75">
                    {cat.skills.map((skill) => (
                      <div
                        key={`${cat.slug}-${skill}`}
                        className="flex max-h-8.75 shrink-0 items-center justify-center rounded-md border border-border bg-contrast/4 px-2.25 py-1"
                      >
                        <span className="whitespace-nowrap text-[13px] font-medium leading-4.5 tracking-[-0.13px] text-foreground">
                          <HighlightedText source="skills">
                            {formatSkillLabel(skill)}
                          </HighlightedText>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SectionBody>
    </SectionCard>
  );
}
